import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default notification preferences
const DEFAULT_PREFERENCES = {
  appointments: true,
  community: true,
  assignments: true,
  announcements: true
};

/**
 * Hook for managing push notifications
 * Handles permission requests, subscription, preferences, and status
 */
export function usePushNotifications() {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkSubscriptionStatus();
    }
  }, []);

  // Check if user is already subscribed and get preferences
  const checkSubscriptionStatus = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
      
      // Fetch preferences from server
      const token = localStorage.getItem('access_token');
      if (token) {
        const response = await fetch(`${API_URL}/api/push/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.preferences) {
            setPreferences(data.preferences);
          }
        }
      }
    } catch (err) {
      console.error('Error checking subscription:', err);
    }
  };

  // Convert base64 to Uint8Array for VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        setError('Notification permission denied. Please allow notifications in your browser.');
        setIsLoading(false);
        return false;
      }

      // Get service worker registration
      let registration;
      try {
        registration = await navigator.serviceWorker.ready;
      } catch (swError) {
        setError('Service worker not available. Please refresh the page and try again.');
        setIsLoading(false);
        return false;
      }

      // Get VAPID public key from server
      const token = localStorage.getItem('access_token');
      let response;
      try {
        response = await fetch(`${API_URL}/api/push/vapid-public-key`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
      } catch (fetchError) {
        setError('Cannot connect to server. Please check your internet connection.');
        setIsLoading(false);
        return false;
      }

      if (!response.ok) {
        setError('Push notification service unavailable. Please try again later.');
        setIsLoading(false);
        return false;
      }

      const { public_key } = await response.json();
      if (!public_key) {
        setError('Push notification service not configured.');
        setIsLoading(false);
        return false;
      }
      
      const applicationServerKey = urlBase64ToUint8Array(public_key);

      // Subscribe to push manager
      let subscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey
        });
      } catch (pushError) {
        console.error('Push subscription error:', pushError);
        setError('Failed to subscribe to push notifications. Please try again.');
        setIsLoading(false);
        return false;
      }

      // Send subscription to server
      const subscribeResponse = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      });

      if (!subscribeResponse.ok) {
        throw new Error('Failed to save subscription on server');
      }

      setIsSubscribed(true);
      setIsLoading(false);
      return true;

    } catch (err) {
      console.error('Push subscription error:', err);
      setError(err.message);
      setIsLoading(false);
      return false;
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!isSupported) return false;

    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from browser
        await subscription.unsubscribe();

        // Remove from server
        const token = localStorage.getItem('access_token');
        await fetch(`${API_URL}/api/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            subscription: subscription.toJSON()
          })
        });
      }

      setIsSubscribed(false);
      setIsLoading(false);
      return true;

    } catch (err) {
      console.error('Push unsubscribe error:', err);
      setError(err.message);
      setIsLoading(false);
      return false;
    }
  }, [isSupported]);

  // Update notification preferences
  const updatePreferences = useCallback(async (newPreferences) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/push/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newPreferences)
      });

      if (!response.ok) {
        throw new Error('Failed to update preferences');
      }

      const data = await response.json();
      setPreferences(data.preferences);
      setIsLoading(false);
      return true;

    } catch (err) {
      console.error('Update preferences error:', err);
      setError(err.message);
      setIsLoading(false);
      return false;
    }
  }, []);

  // Send a test notification
  const sendTestNotification = useCallback(async () => {
    if (!isSubscribed) {
      setError('Please subscribe to notifications first');
      return false;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/push/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to send test notification');
      }

      return true;
    } catch (err) {
      console.error('Test notification error:', err);
      setError(err.message);
      return false;
    }
  }, [isSubscribed]);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    preferences,
    subscribe,
    unsubscribe,
    updatePreferences,
    sendTestNotification,
    checkSubscriptionStatus
  };
}

export default usePushNotifications;
